import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Card, Button, Input, Select, Label, EmptyState } from "../components/ui-kit";
import { SearchSelect } from "../components/SearchSelect";
import { ColumnFilter, DateRangeFilter, distinctOptions } from "../components/ColumnFilter";
import { LazyPhoto } from "../components/LazyPhoto";
import { compressImage } from "../lib/image";
import { Plus, Camera, Image as ImageIcon, CheckCircle2, Clock, XCircle, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const STATUS_STYLE = {
  received: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-800",
  not_received: "bg-red-100 text-red-800",
  declined: "bg-red-100 text-red-800",
};
const STATUS_LABEL = {
  received: "Received",
  pending: "Pending",
  not_received: "Not received",
  declined: "Declined",
};
const STATUS_ICON = {
  received: CheckCircle2,
  pending: Clock,
  not_received: XCircle,
  declined: XCircle,
};

// Convert ISO UTC to IST HH:MM display.
const istTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch {
    return iso.slice(0, 16);
  }
};

export default function CollectionsPage() {
  const { user } = useAuth();
  const canEditStatus = ["admin", "manager"].includes(user?.role);

  const [rows, setRows] = useState([]);
  const [colFilters, setColFilters] = useState({ customer: [], sales: [], status: [], mode: [] });
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const setCol = (k) => (vals) => setColFilters((f) => ({ ...f, [k]: vals }));
  const filteredRows = rows.filter((r) => {
    const d = (r.date || "").slice(0, 10);
    if (dateRange.from && d < dateRange.from) return false;
    if (dateRange.to && d > dateRange.to) return false;
    if (colFilters.customer.length && !colFilters.customer.includes(r.customer_id)) return false;
    if (colFilters.sales.length && !colFilters.sales.includes(r.user_id || "")) return false;
    if (colFilters.status.length && !colFilters.status.includes(r.status || "pending")) return false;
    if (colFilters.mode.length && !colFilters.mode.includes(r.mode || "")) return false;
    return true;
  });
  const activeColFilters = Object.values(colFilters).reduce((a, v) => a + (v.length ? 1 : 0), 0) + (dateRange.from || dateRange.to ? 1 : 0);
  const clearColFilters = () => { setColFilters({ customer: [], sales: [], status: [], mode: [] }); setDateRange({ from: "", to: "" }); };
  const statusRows = rows.map((r) => ({ ...r, status: r.status || "pending", status_label: STATUS_LABEL[r.status || "pending"] || r.status }));
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    customer_id: "", amount: "", mode: "Cash", reference: "", notes: "",
    status: "pending", receipt_photo_b64: "",
    expected_payment_date: "", payment_date: "",
  });
  const [editingStatus, setEditingStatus] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    const [c, cs] = await Promise.all([api.get("/collections"), api.get("/customers")]);
    setRows(c.data);
    setCustomers(cs.data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(() => {
    const t = { received: 0, pending: 0, not_received: 0 };
    for (const r of rows) {
      const st = r.status || "pending";
      if (st === "received") t.received += Number(r.amount || 0);
      else if (st === "not_received" || st === "declined") t.not_received += Number(r.amount || 0);
      else t.pending += Number(r.amount || 0);
    }
    return t;
  }, [rows]);

  const onPhoto = async (file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) return toast.error("Photo too large (max 12MB)");
    try {
      const receipt_photo_b64 = await compressImage(file);
      setForm((f) => ({ ...f, receipt_photo_b64 }));
    } catch {
      toast.error("Could not read photo");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/collections", { ...form, amount: Number(form.amount) });
      toast.success("Collection recorded");
      setOpen(false);
      setForm({ customer_id: "", amount: "", mode: "Cash", reference: "", notes: "", status: "pending", receipt_photo_b64: "", expected_payment_date: "", payment_date: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const saveStatus = async (row, status, remarks) => {
    try {
      await api.put(`/collections/${row.id}/status`, { status, remarks });
      toast.success("Status updated");
      setEditingStatus(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader
        title="Collections"
        subtitle="Payments received from customers"
        actions={
          <Button onClick={() => setOpen((s) => !s)} data-testid="new-collection-btn">
            <Plus className="w-4 h-4" /> New collection
          </Button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card>
          <div className="text-[10px] uppercase tracking-widest text-[#5C6B5E] font-bold">Received</div>
          <div className="text-3xl font-heading font-black text-emerald-700" data-testid="totals-received">{money(totals.received)}</div>
        </Card>
        <Card>
          <div className="text-[10px] uppercase tracking-widest text-[#5C6B5E] font-bold">Pending</div>
          <div className="text-3xl font-heading font-black text-amber-700" data-testid="totals-pending">{money(totals.pending)}</div>
        </Card>
        <Card>
          <div className="text-[10px] uppercase tracking-widest text-[#5C6B5E] font-bold">Not received / Declined</div>
          <div className="text-3xl font-heading font-black text-red-700" data-testid="totals-not-received">{money(totals.not_received)}</div>
        </Card>
      </div>

      {open && (
        <Card className="mb-6">
          <h3 className="font-heading text-xl font-bold mb-4">New collection</h3>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Customer</Label>
              <SearchSelect
                value={form.customer_id}
                onChange={(v) => setForm({ ...form, customer_id: v })}
                required
                testId="new-collection-customer"
                placeholder="Search customer by name or code…"
                options={customers.map((c) => ({
                  value: c.id,
                  label: c.name,
                  sublabel: c.code,
                  keywords: [c.mobile, c.city].filter(Boolean),
                }))}
              />
            </div>
            <div>
              <Label>Amount (₹)</Label>
              <Input type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div>
              <Label>Mode</Label>
              <Select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                <option>Cash</option>
                <option>Cheque</option>
                <option>NEFT/RTGS</option>
                <option>UPI</option>
                <option>Card</option>
              </Select>
            </div>
            <div>
              <Label>Reference</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Cheque # / UPI ID" />
            </div>
            <div>
              <Label>Status</Label>
              {canEditStatus ? (
                <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="new-collection-status">
                  <option value="pending">Pending</option>
                  <option value="received">Received</option>
                  <option value="not_received">Not received</option>
                  <option value="declined">Declined</option>
                </Select>
              ) : (
                <div
                  className="min-h-10 flex items-center px-3 py-2 rounded-lg border border-dashed border-[#E5E2DC] bg-[#F9F8F6] text-sm text-[#5C6B5E] leading-snug"
                  data-testid="new-collection-status-locked"
                >
                  <span><b className="text-[#1A2E1C]">Pending</b> — approved by admin/manager</span>
                </div>
              )}
            </div>
            <div>
              <Label>Expected payment date</Label>
              <Input
                type="date"
                value={form.expected_payment_date}
                onChange={(e) => setForm({ ...form, expected_payment_date: e.target.value })}
                data-testid="collection-expected-date"
              />
            </div>
            {canEditStatus && (
            <div>
              <Label>Actual payment date</Label>
              <Input
                type="date"
                value={form.payment_date}
                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                data-testid="collection-payment-date"
                placeholder="Auto-set on receipt"
              />
            </div>
            )}
            <div>
              <Label>Receipt photo (optional)</Label>
              <div className="flex flex-wrap gap-2">
                <label className="inline-flex items-center gap-2 cursor-pointer bg-[#1E3F20]/10 text-[#1E3F20] hover:bg-[#1E3F20]/20 rounded-lg px-4 py-2 font-semibold text-sm">
                  <Camera className="w-4 h-4" /> Camera
                  <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="collection-photo-camera" onChange={(e) => onPhoto(e.target.files?.[0])} />
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer bg-[#F1EFEA] text-[#1E3F20] hover:bg-[#E5E2DC] rounded-lg px-4 py-2 font-semibold text-sm">
                  <ImageIcon className="w-4 h-4" /> Gallery
                  <input type="file" accept="image/*" className="hidden" data-testid="collection-photo-gallery" onChange={(e) => onPhoto(e.target.files?.[0])} />
                </label>
              </div>
              {form.receipt_photo_b64 && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={form.receipt_photo_b64} alt="Receipt" className="h-20 w-20 rounded object-cover border" />
                  <button type="button" onClick={() => setForm({ ...form, receipt_photo_b64: "" })} className="text-xs text-red-600 hover:underline">Remove</button>
                </div>
              )}
            </div>
            <div className="sm:col-span-2">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="collection-submit">Record</Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        {activeColFilters > 0 && (
          <div className="flex items-center justify-between px-4 py-2 bg-[#F2EDE4] text-xs text-[#1A2E1C] border-b border-[#E5E2DC]" data-testid="collections-colfilter-bar">
            <span><b>{filteredRows.length}</b> of {rows.length} collections · {activeColFilters} column filter{activeColFilters > 1 ? "s" : ""} active</span>
            <button onClick={clearColFilters} className="font-semibold text-[#1E3F20] hover:underline" data-testid="collections-colfilter-clear">Clear all</button>
          </div>
        )}
        {filteredRows.length === 0 ? (
          <div className="p-8">
            <EmptyState title="No collections yet" description={activeColFilters ? "No collections match the column filters." : "Record your first payment to see it here."} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
                <tr className="text-left text-[11px] uppercase tracking-widest text-[#5C6B5E]">
                  <th className="px-4 py-3"><DateRangeFilter label="Date (IST)" range={dateRange} onChange={setDateRange} testid="collections-colfilter-date" /></th>
                  <th className="px-4 py-3">Photo</th>
                  <th className="px-4 py-3">
                    <ColumnFilter label="Customer" options={distinctOptions(rows, "customer_id", "customer_name")} selected={colFilters.customer} onChange={setCol("customer")} testid="collections-colfilter-customer" />
                  </th>
                  <th className="px-4 py-3">
                    <ColumnFilter label="Sales" options={distinctOptions(rows, "user_id", "user_name", "Unknown")} selected={colFilters.sales} onChange={setCol("sales")} testid="collections-colfilter-sales" />
                  </th>
                  <th className="px-4 py-3">
                    <ColumnFilter label="Mode" options={distinctOptions(rows, "mode", "mode", "—")} selected={colFilters.mode} onChange={setCol("mode")} testid="collections-colfilter-mode" />
                  </th>
                  <th className="px-4 py-3">Reference</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3">Payment Date</th>
                  <th className="px-4 py-3">
                    <ColumnFilter label="Status" options={distinctOptions(statusRows, "status", "status_label")} selected={colFilters.status} onChange={setCol("status")} testid="collections-colfilter-status" align="right" />
                  </th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  const st = r.status || "pending";
                  const Icon = STATUS_ICON[st] || Clock;
                  return (
                    <tr key={r.id} className="border-b border-[#E5E2DC] hover:bg-[#F2EDE4]/60" data-testid={`collection-row-${r.id}`}>
                      <td className="px-4 py-3 whitespace-nowrap">{istTime(r.date)}</td>
                      <td className="px-4 py-3">
                        {r.has_receipt_photo || r.receipt_photo_b64 ? (
                          <LazyPhoto
                            kind="collection"
                            id={r.id}
                            alt="Receipt"
                            title={`${r.customer_name} — ${money(r.amount)}`}
                            className="h-10 w-10 rounded border hover:opacity-80"
                            onOpen={(url, title) => setPreview({ url, title })}
                            testid={`collection-photo-thumb-${r.id}`}
                          />
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-[#8A8577]">
                            <ImageIcon className="w-3.5 h-3.5" /> —
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{r.customer_name}</div>
                        <div className="text-xs text-[#5C6B5E]">{r.customer_code}</div>
                      </td>
                      <td className="px-4 py-3 text-[#5C6B5E]">{r.user_name}</td>
                      <td className="px-4 py-3">{r.mode}</td>
                      <td className="px-4 py-3 text-[#5C6B5E]">{r.reference}</td>
                      <td className="px-4 py-3 text-right font-semibold">{money(r.amount)}</td>
                      <td className="px-4 py-3 text-xs" data-testid={`collection-paydate-${r.id}`}>
                        {r.payment_date ? (
                          <span className="text-emerald-800 font-semibold">{r.payment_date}</span>
                        ) : r.expected_payment_date ? (
                          <span className="text-amber-800">exp: {r.expected_payment_date}</span>
                        ) : (
                          <span className="text-[#8A8577]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${STATUS_STYLE[st]}`}
                          data-testid={`collection-status-${r.id}`}
                        >
                          <Icon className="w-3 h-3" /> {STATUS_LABEL[st]}
                        </span>
                        {r.status_remarks && (
                          <div className="text-[11px] text-[#5C6B5E] mt-1 max-w-[200px]">{r.status_remarks}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canEditStatus && (
                          <button
                            onClick={() => setEditingStatus(r)}
                            data-testid={`edit-status-${r.id}`}
                            className="text-xs px-2.5 py-1 rounded bg-[#1E3F20]/10 text-[#1E3F20] hover:bg-[#1E3F20]/20 font-semibold"
                          >
                            Change status
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editingStatus && (
        <StatusModal
          row={editingStatus}
          onClose={() => setEditingStatus(null)}
          onSave={saveStatus}
        />
      )}
      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="collection-photo-modal"
        >
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-200"
              data-testid="collection-photo-modal-close"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="text-white mb-2 text-sm font-semibold">{preview.title}</div>
            <img src={preview.url} alt="Receipt" className="w-full max-h-[80vh] object-contain rounded-lg bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}

function StatusModal({ row, onClose, onSave }) {
  const [status, setStatus] = useState(row.status || "pending");
  const [remarks, setRemarks] = useState(row.status_remarks || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if ((status === "not_received" || status === "declined") && !remarks.trim()) {
      return toast.error("Please add a reason");
    }
    setBusy(true);
    try {
      await onSave(row, status, remarks);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="status-modal">
        <div className="p-5 border-b border-[#E5E2DC] font-heading text-xl font-bold">Change status</div>
        <div className="p-5 space-y-4">
          <div className="text-sm text-[#5C6B5E]"><b>{row.customer_name}</b> · {money(row.amount)}</div>
          <div>
            <Label>New status</Label>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="status-select">
              <option value="pending">Pending</option>
              <option value="received">Received</option>
              <option value="not_received">Not received</option>
              <option value="declined">Declined</option>
            </Select>
          </div>
          <div>
            <Label>Remarks {(status === "not_received" || status === "declined") && <span className="text-red-600">*</span>}</Label>
            <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="status-remarks" />
          </div>
        </div>
        <div className="p-5 border-t border-[#E5E2DC] flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="status-save">{busy ? "Saving…" : "Save"}</Button>
        </div>
      </div>
    </div>
  );
}
